import React from 'react';
import { NavLink } from 'react-router-dom';
import SideNavBar from './SideNavBar';
import { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';

function Finance() {
  const [recentTransactions, setRecentTransactions] = useState([
    { category: 'Tithe', amount: 12000, date: '2025-07-12' },
    { category: 'Welfare', amount: 5000, date: '2025-07-10' },
    { category: 'Offering', amount: 8000, date: '2025-07-09' },
  ]);

  const handleDeleteTransaction = indexToRemove => {
    setRecentTransactions(prev => prev.filter((_, i) => i !== indexToRemove));
  };

  const { isDark } = useTheme();

  return (
    <Layout title="Finance Dashboard">

    
    <div
      className={isDark ? 'dark bg-gray-900 min-h-screen h-full' : 'bg-white min-h-screen h-full'}
    >
    
      <div className="flex h-screen">
        <div
          className={`hidden md:block fixed top-0 left-0 h-screen w-52 overflow-y-auto bg-gradient-to-r ${
            isDark ? 'from-gray-900 to-gray-800' : 'from-green-800 to-yellow-200'
          } p-3 font-bold custom-scrollbar z-50`}
        >
          <SideNavBar isDark={isDark} />
        </div>


        {/* Main Content */}
        <div className="flex-1 md:ml-52 overflow-y-auto p-4">
          {/* Header */}
          <div className="bg-green-800 rounded-t-xl p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="w-full md:w-auto">
              <h1 className="text-white text-2xl md:text-3xl font-bold">Financial Dashboard</h1>
              <p className="text-white text-sm md:text-xs font-semibold mt-2 md:mt-3 mb-4">
                Manage and view all financial records in one place. Track income, expenses, and
                generate reports.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                <NavLink>
                  <span className="bg-gray-200 text-green-900 font-semibold rounded px-2 py-1 text-xs">
                    April 2025
                  </span>
                </NavLink>
                <NavLink>
                  <span className="bg-gray-200 text-green-900 font-semibold rounded px-2 py-1 text-xs">
                    Financial Health: Good
                  </span>
                </NavLink>
                <NavLink>
                  <span className="bg-gray-200 text-green-900 font-semibold rounded px-2 py-1 text-xs">
                    Balance: 5,000
                  </span>
                </NavLink>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="w-full md:w-auto flex flex-wrap gap-2 justify-start md:justify-end">
              <NavLink to="/addfinancialtransaction">
                <span className="text-xs text-green-800 font-bold p-2 rounded-md bg-white">
                  Add Transaction
                </span>
              </NavLink>
              <NavLink to="/transactionrecords">
                <span className="text-xs text-green-800 font-bold p-2 rounded-md bg-gray-200">
                  Transactions
                </span>
              </NavLink>
              <NavLink to="/tithetracking">
                <span className="text-xs text-green-800 font-bold p-2 rounded-md bg-gray-200">
                  Tithes
                </span>
              </NavLink>
              <NavLink to="/welfare">
                <span className="text-xs text-green-800 font-bold p-2 rounded-md bg-gray-200">
                  Welfare
                </span>
              </NavLink>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-white shadow-2xl rounded-xl mt-4 p-4 md:p-6">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">
              Income vs Expenses
            </h1>

            <div className="w-full overflow-x-auto">
              <div className="flex min-w-[768px] md:min-w-0 gap-4 md:gap-6 items-end h-72 bg-gray-100 px-4 py-6 rounded-lg overflow-x-auto md:overflow-x-hidden">
                {[
                  { month: 'Jan', income: 4000, expenses: 2000 },
                  { month: 'Feb', income: 3000, expenses: 1800 },
                  { month: 'Mar', income: 3500, expenses: 2400 },
                  { month: 'Apr', income: 5000, expenses: 2500 },
                  { month: 'May', income: 3800, expenses: 2600 },
                  { month: 'Jun', income: 4500, expenses: 2900 },
                  { month: 'Jul', income: 4700, expenses: 3000 },
                  { month: 'Aug', income: 4300, expenses: 2800 },
                  { month: 'Sep', income: 3900, expenses: 2200 },
                  { month: 'Oct', income: 4800, expenses: 2700 },
                  { month: 'Nov', income: 5200, expenses: 3100 },
                  { month: 'Dec', income: 5500, expenses: 3300 },
                ].map((item, idx) => {
                  const max = 5500;
                  const incomeHeight = `${(item.income / max) * 100}%`;
                  const expensesHeight = `${(item.expenses / max) * 100}%`;
                  const isCurrentMonth = new Date().getMonth() === idx;

                  return (
                    <div
                      key={idx}
                      className="flex flex-col items-center w-8 md:w-10 lg:w-12 text-center"
                    >
                      {/* Bar wrapper */}
                      <div className="relative h-40 flex justify-center items-end gap-1 group overflow-visible">
                        {/* Income Bar */}
                        <div
                          className="w-1.5 md:w-2 lg:w-3 bg-green-500 rounded-t transition-all duration-700 ease-out relative"
                          style={{ height: incomeHeight }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition duration-200 whitespace-nowrap z-10">
                            Income: ₦{item.income}
                          </div>
                        </div>

                        {/* Expense Bar */}
                        <div
                          className="w-1.5 md:w-2 lg:w-3 bg-red-500 rounded-t transition-all duration-700 ease-out relative"
                          style={{ height: expensesHeight }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition duration-200 whitespace-nowrap z-10">
                            Expenses: ₦{item.expenses}
                          </div>
                        </div>
                      </div>

                      {/* Month label */}
                      <span
                        className={`mt-2 text-[10px] md:text-xs font-semibold ${
                          isCurrentMonth ? 'text-green-600 underline' : 'text-gray-700'
                        }`}
                      >
                        {item.month}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white shadow-2xl rounded-xl mt-6 p-4 md:p-6">
              <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
                Income by Category
              </h1>

              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Pie Chart */}
                <div className="relative w-40 h-40 md:w-52 md:h-52 md:mt-4 rounded-full overflow-hidden">
                  <style>
                    {`
      @keyframes drawSlice {
        0% {
          stroke-dasharray: 0 100;
        }
        100% {
          stroke-dasharray: var(--size) 100;
        }
      }
      .slice {
        animation: drawSlice 1.5s ease-out forwards;
      }
    `}
                  </style>

                  <svg viewBox="0 0 32 32" className="w-full h-full rotate-[-90deg]">
                    <circle r="16" cx="16" cy="16" fill="#e5e7eb" />

                    {/* Calculated percentages and cumulative rotations */}
                    {/* Tithes - 31.4% (starts at 0°) */}
                    <circle
                      r="16"
                      cx="16"
                      cy="16"
                      fill="transparent"
                      stroke="#3b82f6"
                      strokeWidth="32"
                      strokeDasharray="31.4 68.6"
                      transform="rotate(0 16 16)"
                      className="slice"
                      style={{ '--size': '31.4' }}
                    />

                    {/* Offerings - 25.1% (starts after 31.4%) */}
                    <circle
                      r="16"
                      cx="16"
                      cy="16"
                      fill="transparent"
                      stroke="#10b981"
                      strokeWidth="32"
                      strokeDasharray="25.1 74.9"
                      transform="rotate(113.04 16 16)"
                      className="slice"
                      style={{ '--size': '25.1' }}
                    />

                    {/* Donations - 18.8% (starts after 56.5%) */}
                    <circle
                      r="16"
                      cx="16"
                      cy="16"
                      fill="transparent"
                      stroke="#8b5cf6"
                      strokeWidth="32"
                      strokeDasharray="18.8 81.2"
                      transform="rotate(203.88 16 16)"
                      className="slice"
                      style={{ '--size': '18.8' }}
                    />

                    {/* Welfare - 12.5% (starts after 75.3%) */}
                    <circle
                      r="16"
                      cx="16"
                      cy="16"
                      fill="transparent"
                      stroke="#f97316"
                      strokeWidth="32"
                      strokeDasharray="12.5 87.5"
                      transform="rotate(271.56 16 16)"
                      className="slice"
                      style={{ '--size': '12.5' }}
                    />

                    {/* Others - 12.2% (starts after 87.8%) */}
                    <circle
                      r="16"
                      cx="16"
                      cy="16"
                      fill="transparent"
                      stroke="#6b7280"
                      strokeWidth="32"
                      strokeDasharray="12.2 87.8"
                      transform="rotate(316.56 16 16)"
                      className="slice"
                      style={{ '--size': '12.2' }}
                    />
                  </svg>
                </div>

                {/* Legend */}
                <div className="space-y-2 mt-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span>Tithes - ₦1,000</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Offerings - ₦800</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span>Donations - ₦600</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span>Welfare - ₦400</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-700 rounded-full"></div>
                    <span>Others - ₦200</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Financial Insights Chart */}
            <div
              className={`mt-6 p-4 md:p-6 rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}
            >
              <h2 className="text-xl md:text-2xl font-bold mb-4">Financial Insights</h2>

              {/* Set total max value for scaling */}
              {(() => {
                const totalIncome = 52000;
                const totalExpenses = 30000;
                const savings = totalIncome - totalExpenses;
                const maxValue = Math.max(totalIncome, totalExpenses, savings);

                const incomePercent = (totalIncome / maxValue) * 100;
                const expensesPercent = (totalExpenses / maxValue) * 100;
                const savingsPercent = (savings / maxValue) * 100;

                return (
                  <div className="space-y-4">
                    {/* Income */}
                    <div>
                      <div className="flex justify-between text-sm font-medium mb-1">
                        <span>Income</span>
                        <span>₦{totalIncome.toLocaleString()}</span>
                      </div>
                      <div className="h-4 rounded bg-green-100 dark:bg-green-900 overflow-hidden">
                        <div
                          className="h-full bg-green-500 transition-all duration-700"
                          style={{ width: `${incomePercent}%` }}
                        />
                      </div>
                    </div>

                    {/* Expenses */}
                    <div>
                      <div className="flex justify-between text-sm font-medium mb-1">
                        <span>Expenses</span>
                        <span>₦{totalExpenses.toLocaleString()}</span>
                      </div>
                      <div className="h-4 rounded bg-red-100 dark:bg-red-900 overflow-hidden">
                        <div
                          className="h-full bg-red-500 transition-all duration-700"
                          style={{ width: `${expensesPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* Savings */}
                    <div>
                      <div className="flex justify-between text-sm font-medium mb-1">
                        <span>Savings</span>
                        <span>₦{savings.toLocaleString()}</span>
                      </div>
                      <div className="h-4 rounded bg-blue-100 dark:bg-blue-900 overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-700"
                          style={{ width: `${savingsPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* Recent Transactions */}
            <div
              className={`mt-6 p-4 md:p-6 rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}
            >
              <h2 className="text-xl md:text-2xl font-bold mb-4">Recent Transactions</h2>

              <div className="space-y-3">
                {recentTransactions.map((tx, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{tx.category}</span>
                      <span className="text-sm opacity-70">{tx.date}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">₦{tx.amount.toLocaleString()}</span>
                      <button
                        onClick={() => handleDeleteTransaction(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        {/* Trash Icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                {recentTransactions.length === 0 && (
                  <p className="text-center text-sm opacity-70 italic">No recent transactions.</p>
                )}
              </div>
            </div>

            {/* Top Tithe Contributors */}
            <div
              className={`mt-6 p-4 md:p-6 rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}
            >
              <h2 className="text-xl md:text-2xl font-bold mb-4">Top Tithe Contributors</h2>

              <div className="space-y-3">
                {[
                  { name: 'John Doe', amount: 15000 },
                  { name: 'Jane Smith', amount: 12000 },
                  { name: 'David Johnson', amount: 10000 },
                  { name: 'Mary Williams', amount: 9000 },
                  { name: 'James Brown', amount: 8500 },
                ].map((contributor, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-white ${
                          index === 0
                            ? 'bg-yellow-500'
                            : index === 1
                            ? 'bg-gray-400'
                            : index === 2
                            ? 'bg-orange-400'
                            : 'bg-blue-500'
                        }`}
                      >
                        {index + 1}
                      </div>
                      <span className="text-sm md:text-base font-medium">{contributor.name}</span>
                    </div>
                    <span className="text-sm md:text-base font-semibold">
                      ₦{contributor.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Layout>
  );
}

export default Finance;
